import { useMemo } from 'react'
import { FakeLink } from '../../../FbUI/FbLink/styles'
import { FilterChipZipRange } from './FilterChipZipRange'
import { FilterChipValue } from './FilterChipValue'
import {
  FilterCriteria,
  FilterStore,
  RangeFilterState,
  SearchFilterState,
  ValueFilterState,
  ZipCodeFilterState,
} from '../../FilterStore'
import { FilterChipRange } from './FilterChipRange'
import { FilterChipSearch } from './FilterChipSearch'
import { FilterIdentifier } from 'models/saved_view'
import { FILTER_TITLES } from '../../filterTitles'
import { cn } from 'components/UI/cn'

export function FilterChips({
  clearAllButton = false,
  showActive = false,
  store,
  identifier,
  className,
  excludeKeys,
}: {
  clearAllButton?: boolean
  showActive?: boolean
  store: FilterStore
  identifier: FilterIdentifier
  className?: string
  /** Filter keys to suppress from chip rendering (e.g. when a filter is
   *  already surfaced elsewhere on the page, like a segmented button group). */
  excludeKeys?: string[]
}) {
  const {
    pendingCriteria,
    criteria,
    pendingValueFilters,
    valueFilters,
    rangeFilters,
    pendingRangeFilters,
    clearPendingValueFilter,
    clearValueFilter,
    clearPendingRangeFilter,
    clearRangeFilter,
    clearAllFilters,
    clearAllPendingFilters,
    pendingZipCodeFilter,
    zipCodeFilter,
    clearPendingZipCodeFilter,
    clearZipCodeFilter,
    search,
    pendingSearch,
    clearSearch,
    clearPendingSearch,
    filterTitles,
  } = store()

  let _valueFilters = pendingValueFilters
  let _rangeFilters = pendingRangeFilters
  let _zipCodeFilter = pendingZipCodeFilter
  let _clearValueFilter = clearPendingValueFilter
  let _clearRangeFilter = clearPendingRangeFilter
  let _search = pendingSearch
  let _clearSearch = clearPendingSearch
  let _criteria = pendingCriteria
  let _clearAll = clearAllPendingFilters
  let _clearZipCodeFilter = clearPendingZipCodeFilter

  if (showActive) {
    _valueFilters = valueFilters
    _rangeFilters = rangeFilters
    _zipCodeFilter = zipCodeFilter
    _clearValueFilter = clearValueFilter
    _clearRangeFilter = clearRangeFilter
    _search = search
    _clearSearch = clearSearch
    _criteria = criteria
    _clearAll = clearAllFilters
    _clearZipCodeFilter = clearZipCodeFilter
  }

  const excluded = new Set(excludeKeys ?? [])
  const filteredValueFilters = excluded.size
    ? Object.fromEntries(
        Object.entries(_valueFilters ?? {}).filter(([k]) => !excluded.has(k))
      )
    : _valueFilters
  const filteredRangeFilters = excluded.size
    ? Object.fromEntries(
        Object.entries(_rangeFilters ?? {}).filter(([k]) => !excluded.has(k))
      )
    : _rangeFilters

  return (
    <FilterChipsComponent
      className={className}
      filterState={{
        valueFilters: filteredValueFilters,
        rangeFilters: filteredRangeFilters,
        zipCodeFilter: _zipCodeFilter,
        search: _search,
        criteria: _criteria,
      }}
      actions={{
        clearValueFilter: _clearValueFilter,
        clearSearch: _clearSearch,
        clearAll: clearAllButton ? _clearAll : undefined,
        clearZipCodeFilter: _clearZipCodeFilter,
        clearRangeFilter: _clearRangeFilter,
      }}
      identifier={identifier}
      filterTitles={filterTitles}
    />
  )
}

export function FilterChipsComponent({
  filterState,
  actions,
  identifier,
  className,
  filterTitles,
  isHovered,
}: {
  filterState: {
    valueFilters?: ValueFilterState
    rangeFilters?: RangeFilterState
    zipCodeFilter?: ZipCodeFilterState
    search?: SearchFilterState
    criteria?: FilterCriteria
  }
  actions?: {
    clearZipCodeFilter?: () => void
    clearRangeFilter?: (key: string) => void
    clearValueFilter?: (key: string) => void
    clearSearch?: (key: string) => void
    clearAll?: () => void
  }
  identifier: FilterIdentifier
  className?: string
  filterTitles?: Record<string, string>
  isHovered?: boolean
}) {
  const totalFilterCount = useMemo(
    () =>
      Object.keys(filterState?.valueFilters ?? {}).length +
      Object.keys(filterState?.rangeFilters ?? {}).length +
      Object.values(filterState?.search ?? {}).filter((s) => !!s.value).length +
      (filterState?.zipCodeFilter?.[0] ? 1 : 0),
    [filterState]
  )

  if (totalFilterCount === 0) return null

  const handleClearZipCodeFilter = () => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearZipCodeFilter?.()
  }

  const handleClearRangeFilter = (key: string) => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearRangeFilter?.(key)
  }

  const handleClearValueFilter = (key: string) => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearValueFilter?.(key)
  }

  const handleClearSearch = (key: string) => {
    if (totalFilterCount === 1) {
      actions?.clearAll?.()
      return
    }

    actions?.clearSearch?.(key)
  }

  return (
    <div
      className={cn(
        'd-flex flex-wrap align-items-center gap-2 ml-auto mt-2.5',
        className
      )}
    >
      {filterState?.zipCodeFilter?.[0] && (
        <FilterChipZipRange
          label="Zip Code"
          active={filterState.zipCodeFilter}
          isExclusive={filterState.criteria === 'neither'}
          isHovered={isHovered}
          onClose={
            actions?.clearZipCodeFilter ? handleClearZipCodeFilter : undefined
          }
        />
      )}

      {Object.entries(filterState?.rangeFilters ?? {}).map(([key, value]) => {
        return (
          <FilterChipRange
            key={key}
            filterCriteria={filterState?.criteria ?? 'all'}
            title={
              filterTitles?.[key] || FILTER_TITLES?.[identifier]?.[key] || key
            }
            isHovered={isHovered}
            onClose={
              actions?.clearRangeFilter
                ? () => handleClearRangeFilter(key)
                : undefined
            }
            value={value}
          />
        )
      })}

      {Object.entries(filterState?.valueFilters ?? {}).map(([key, value]) => (
        <FilterChipValue
          key={key}
          filterCriteria={filterState?.criteria ?? 'all'}
          title={
            filterTitles?.[key] || FILTER_TITLES?.[identifier]?.[key] || key
          }
          isHovered={isHovered}
          onClose={
            actions?.clearValueFilter
              ? () => handleClearValueFilter(key)
              : undefined
          }
          value={value}
        />
      ))}

      {Object.keys(filterState?.search ?? {}).map((key) => (
        <FilterChipSearch
          key={key}
          filterCriteria={filterState?.criteria ?? 'all'}
          search={filterState?.search?.[key]?.value ?? ''}
          searchLabel={filterState?.search?.[key]?.label ?? ''}
          isHovered={isHovered}
          onClose={
            actions?.clearSearch ? () => handleClearSearch(key) : undefined
          }
        />
      ))}

      {actions?.clearAll && totalFilterCount > 0 && (
        <FakeLink className={'ml-2'} onClick={actions?.clearAll}>
          Clear all
        </FakeLink>
      )}
    </div>
  )
}
